import { useCallback, useEffect, useMemo, useState } from 'react';

import { CUSTOM_PERIOD_VALUE } from '../components/EvaluationDatePeriod';

import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';
import type { EvaluationPeriod } from '@/types/manager-assessment/Evaluation';

import { useSaveEvaluationPeriod } from '@/api/cycle-evaluation-services/manager-assessment';
import { useHydrationTracking } from '@/hooks/useDirtyState';
import { useError } from '@/hooks/utils/useError';
import { parseServerInstant } from '@/utils/dateUtils';

export const useEvaluationPeriod = (
    cycleInstanceId: string | undefined,
    cycleInstance: CycleInstance | undefined,
    periods: EvaluationPeriod[],
    isReadOnly: boolean = false,
) => {
    const { handleError } = useError();
    const { mutateAsync: saveEvaluationPeriod } = useSaveEvaluationPeriod();

    const [selectedPeriod, setSelectedPeriod] = useState('');
    const [evaluationDate, setEvaluationDate] = useState<Date>(new Date());
    const [customFromDate, setCustomFromDate] = useState<Date | undefined>(undefined);
    const [customToDate, setCustomToDate] = useState<Date | undefined>(undefined);

    const [isPeriodInherited, setIsPeriodInherited] = useState(false);

    const isCustomPeriodInvalid = useMemo(
        () =>
            selectedPeriod === CUSTOM_PERIOD_VALUE &&
            customFromDate !== undefined &&
            customToDate !== undefined &&
            customToDate.getTime() < customFromDate.getTime(),
        [selectedPeriod, customFromDate, customToDate],
    );

    const { dirtyRef } = useHydrationTracking();

    // Hydrate from CycleInstance once
    useEffect(() => {
        // removed hydratedRef.current for now to re-ren the effect and re assign the new updated value from handout header in prepare meeting page
        if (!cycleInstance) {
            return;
        }
        const storedEvaluationDate = cycleInstance.evaluationDate
            ? parseServerInstant(cycleInstance.evaluationDate)
            : undefined;
        if (storedEvaluationDate) {
            setEvaluationDate(storedEvaluationDate);
        }

        const hasOwnPeriod = Boolean(cycleInstance.evaluationPeriodId);
        const periodId = hasOwnPeriod
            ? cycleInstance.evaluationPeriodId
            : cycleInstance.groupEvaluationPeriodId;
        const periodStart = hasOwnPeriod
            ? cycleInstance.evaluationPeriodStart
            : cycleInstance.groupEvaluationPeriodStart;
        const periodEnd = hasOwnPeriod
            ? cycleInstance.evaluationPeriodEnd
            : cycleInstance.groupEvaluationPeriodEnd;

        const matchesGroup =
            Boolean(cycleInstance.groupEvaluationPeriodId) &&
            cycleInstance.evaluationPeriodId === cycleInstance.groupEvaluationPeriodId &&
            cycleInstance.evaluationPeriodStart === cycleInstance.groupEvaluationPeriodStart &&
            cycleInstance.evaluationPeriodEnd === cycleInstance.groupEvaluationPeriodEnd;
        const isInheritedFromGroup = hasOwnPeriod
            ? matchesGroup
            : Boolean(cycleInstance.groupEvaluationPeriodId);

        if (periodId) {
            setSelectedPeriod(periodId);
            setIsPeriodInherited(isInheritedFromGroup);
        }
        if (periodStart) {
            setCustomFromDate(new Date(periodStart));
        }
        if (periodEnd) {
            setCustomToDate(new Date(periodEnd));
        }
        // hydratedRef.current = true;
    }, [cycleInstance]);

    // Auto-save on change (debounced)
    useEffect(() => {
        if (!cycleInstanceId || !dirtyRef.current || isReadOnly || isCustomPeriodInvalid) {
            return;
        }
        const isCustom = selectedPeriod === CUSTOM_PERIOD_VALUE;
        // A predefined period carries its own from/to; a custom one uses the picked range.
        const predefined = periods.find((period) => period.id === selectedPeriod);
        const evaluationPeriodStart = isCustom
            ? (customFromDate?.toISOString() ?? null)
            : predefined
              ? new Date(predefined.startDate).toISOString()
              : null;
        const evaluationPeriodEnd = isCustom
            ? (customToDate?.toISOString() ?? null)
            : predefined
              ? new Date(predefined.endDate).toISOString()
              : null;

        const timeout = setTimeout(() => {
            void saveEvaluationPeriod({
                cycleInstanceId,
                payload: {
                    evaluationDate: evaluationDate.toISOString(),
                    evaluationPeriodId: selectedPeriod || null,
                    evaluationPeriodStart,
                    evaluationPeriodEnd,
                },
            }).catch((error) => handleError({ error }));
        }, 600);
        return () => clearTimeout(timeout);
    }, [
        evaluationDate,
        selectedPeriod,
        customFromDate,
        customToDate,
        periods,
        cycleInstanceId,
        saveEvaluationPeriod,
        handleError,
        dirtyRef,
        isReadOnly,
        isCustomPeriodInvalid,
    ]);

    const handleEvaluationDateChange = useCallback(
        (date: Date) => {
            dirtyRef.current = true;
            setEvaluationDate(date);
        },
        [dirtyRef],
    );

    const handlePeriodChange = useCallback(
        (value: string) => {
            dirtyRef.current = true;

            setIsPeriodInherited(false);
            setSelectedPeriod(value);
            // When switching to a predefined period, populate custom dates with that period's dates
            // so they're available if the user switches back to custom
            if (value !== CUSTOM_PERIOD_VALUE) {
                const predefined = periods.find((period) => period.id === value);
                if (predefined) {
                    setCustomFromDate(new Date(predefined.startDate));
                    setCustomToDate(new Date(predefined.endDate));
                }
            }
        },
        [dirtyRef, periods],
    );

    const handleCustomFromChange = useCallback(
        (date?: Date) => {
            dirtyRef.current = true;
            setIsPeriodInherited(false);
            setCustomFromDate(date);
        },
        [dirtyRef],
    );

    const handleCustomToChange = useCallback(
        (date?: Date) => {
            dirtyRef.current = true;
            setIsPeriodInherited(false);
            setCustomToDate(date);
        },
        [dirtyRef],
    );

    return {
        selectedPeriod,
        evaluationDate,
        customFromDate,
        customToDate,
        isPeriodInherited,
        isCustomPeriodInvalid,
        handleEvaluationDateChange,
        handlePeriodChange,
        handleCustomFromChange,
        handleCustomToChange,
    };
};
